import {
  Button,
  ButtonType,
  Icon,
  IconButton,
  IconButtonType,
  Size,
  TextField,
  TextFieldType,
  TextIconColor
} from '../../../../core';

export const keyValueStyling = 'flex w-full flex-row gap-4';
export const keyValuePairStyling = 'flex w-full flex-col gap-3';

export interface KeyValuePairProps<T> {
  disabled: boolean;
  index: number;
  key: string;
  onKeyChange: (newKey: string) => void;
  onRemove: () => void;
  onValueChange: (newValue: T) => void;
  renderValueInput: (props: {
    index: number;
    onChange: (value: T) => void;
    value: T;
  }) => React.ReactNode;
  value: T;
  onKeyBlur?: () => void;
}

export const renderKeyValuePair = <T,>({
  disabled,
  key,
  value,
  index,
  onKeyChange,
  onKeyBlur,
  onRemove,
  onValueChange,
  renderValueInput
}: KeyValuePairProps<T>) => (
  <div
    className={keyValueStyling}
    data-testid="key-value-container"
    key={index}
  >
    <TextField
      disabled={disabled}
      fullWidth
      onBlur={onKeyBlur}
      onChange={onKeyChange}
      placeholder="Key"
      type={key ? TextFieldType.DEFAULT : TextFieldType.HIGHLIGHT}
      value={key}
    />
    {renderValueInput({ value, onChange: onValueChange, index })}
    <IconButton
      color={TextIconColor.PRIMARY}
      disabled={disabled}
      icon={Icon.TRASH}
      onClick={onRemove}
      size={Size.SMALL}
      type={IconButtonType.OUTLINED}
    />
  </div>
);

export interface KeyValueSectionProps<T> {
  disabled: boolean;
  entries: [string, T][];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, key: string, value: T) => void;
  renderValueInput: (props: {
    index: number;
    onChange: (value: T) => void;
    value: T;
  }) => React.ReactNode;
}

const WorkflowApiActionStepEditorKeyValueSection = <T,>({
  disabled,
  entries,
  onUpdate,
  onRemove,
  onAdd,
  renderValueInput
}: KeyValueSectionProps<T>) => {
  /*
   * Note: Append index as a suffix to ensure unique keys while typing.
   *
   * For context, in our redux store, we are storing headers and parameters as dicts,
   * and the redux store is responsible for showing the changes to headers/parameters.
   * Due to the fact that they are dicts and it being updated on onChange of the key
   * or value field, we are getting a bug where the old key-value is over-ridden by
   * the transient newKey that matches the old key.
   *
   * This fix enforces uniqueness during typing by appending the index to the key,
   * and then on blur, removes the suffix for deduplication of keys. Although it might
   * not be the cleanest way to fix this, it is the most straightforward solution.
   */
  const handleKeyChange = (index: number, newKey: string) => {
    if (!entries[index]) return;
    const [_, value] = entries[index];

    onUpdate(index, `${newKey}:${index}`, value);
  };

  /*
   * Note: Clean up the key (remove `:${index}`) on blur.
   * This ensures deduplication of keys post typing.
   */
  const handleKeyBlur = (index: number) => {
    if (!entries[index]) return;
    const [key, value] = entries[index];

    onUpdate(index, key.split(':')[0] || key, value);
  };

  return (
    <div className={keyValuePairStyling} data-testid="key-value-pair-container">
      {entries.map(([key, value], index) =>
        renderKeyValuePair({
          key: key.split(':')[0] || key,
          value,
          index,
          onKeyChange: (newKey) => handleKeyChange(index, newKey),
          onKeyBlur: () => handleKeyBlur(index),
          onValueChange: (newValue) => onUpdate(index, key, newValue),
          onRemove: () => onRemove(index),
          renderValueInput,
          disabled
        })
      )}
      <Button
        disabled={disabled}
        endIcon={Icon.PLUS}
        fullWidth
        label="Add Another"
        onClick={onAdd}
        type={ButtonType.SOLID}
      />
    </div>
  );
};

export default WorkflowApiActionStepEditorKeyValueSection;
